/**
 * Phase 1.7 — Logs module index, v2.
 *
 * Group-by: log_type (Maintenance / Pasture / Vet / Animal Care). Logs
 * are dense by nature — list view is the default; the grid view shows
 * compact cards.
 */

import { useEffect, useMemo, useState } from 'react';
import { ScrollText, Plus, Trash2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, EmptyState, FilterBar, KpiCard, SectionHeader, ViewToggle, type ViewMode } from '@/components/v2';
import { authFetch } from '@/lib/auth';
import { useUiPrefs } from '@/lib/layout-flag';
import { usePageHeader } from '@/components/v2/layout/Header';
import { QuickLogSheet } from '@/components/QuickLogSheet';
import type { LogEntry } from '@shared/types';

type LogType = LogEntry['log_type'];
const TYPE_ORDER: LogType[] = ['maintenance', 'pasture', 'vet', 'animal-care'];
const TYPE_LABEL: Record<LogType, string> = {
  maintenance: 'Maintenance',
  pasture: 'Pasture',
  vet: 'Vet',
  'animal-care': 'Animal Care',
};
const TYPE_TONE: Record<LogType, string> = {
  maintenance: 'bg-sky-muted/10 text-sky-muted dark:bg-sky-muted-dark/20 dark:text-sky-muted-dark',
  pasture: 'bg-forest/10 text-forest dark:bg-forest-dark/20 dark:text-forest-dark',
  vet: 'bg-moss/10 text-moss dark:bg-moss-dark/20 dark:text-moss-dark',
  'animal-care': 'bg-burnt-orange/10 text-burnt-orange dark:bg-burnt-orange-dark/20 dark:text-burnt-orange-dark',
};

const VIEW_KEY = 'logs';

export function LogsPageV2(): JSX.Element {
  const { prefs, patch } = useUiPrefs();
  const view: ViewMode = prefs.view_modes?.[VIEW_KEY] ?? 'list';
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<'all' | LogType>('all');
  const [quickLogOpen, setQuickLogOpen] = useState<boolean>(false);

  usePageHeader(
    'Logs',
    <Button
      size="sm"
      onClick={() => setQuickLogOpen(true)}
      className="bg-burnt-orange text-white hover:bg-burnt-orange/90 dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90"
    >
      <Plus className="h-4 w-4" />
      Log activity
    </Button>,
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    async function load(): Promise<void> {
      try {
        const data = await authFetch<{ items: LogEntry[] }>('/api/logs');
        if (!cancelled) setLogs(data.items ?? []);
      } catch (err) {
        if (!cancelled) setError((err as Error).message ?? 'Failed to load logs');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => { cancelled = true; };
    // QuickLog submission doesn't notify the page, so we re-fetch when the
    // sheet closes (assumes the user submitted; cheap enough either way).
  }, [quickLogOpen]);

  const filtered = useMemo<LogEntry[]>(() => {
    const q = search.trim().toLowerCase();
    return logs.filter((l) => {
      if (q && !`${l.description} ${l.entity_name ?? ''}`.toLowerCase().includes(q)) return false;
      if (typeFilter !== 'all' && l.log_type !== typeFilter) return false;
      return true;
    });
  }, [logs, search, typeFilter]);

  const grouped = useMemo<Record<LogType, LogEntry[]>>(() => {
    const out: Record<LogType, LogEntry[]> = {
      maintenance: [], pasture: [], vet: [], 'animal-care': [],
    };
    for (const l of filtered) out[l.log_type].push(l);
    // sort newest-first within each group
    for (const k of TYPE_ORDER) {
      out[k].sort((a, b) => (b.date ?? b.created_at).localeCompare(a.date ?? a.created_at));
    }
    return out;
  }, [filtered]);

  const totals = useMemo(() => {
    const counts = { maintenance: 0, pasture: 0, vet: 0, 'animal-care': 0 } as Record<LogType, number>;
    for (const l of logs) counts[l.log_type] += 1;
    return counts;
  }, [logs]);

  const setView = (next: ViewMode): void => {
    void patch({ view_modes: { ...(prefs.view_modes ?? {}), [VIEW_KEY]: next } });
  };

  const hasActiveFilters = Boolean(search) || typeFilter !== 'all';
  const clear = (): void => { setSearch(''); setTypeFilter('all'); };

  return (
    <div className="space-y-6">
      <QuickLogSheet open={quickLogOpen} onOpenChange={setQuickLogOpen} />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {TYPE_ORDER.map((t) => (
          <KpiCard key={t} label={TYPE_LABEL[t]} primary={{ label: 'Entries', value: totals[t] }} />
        ))}
      </div>

      <FilterBar
        search={{ value: search, onChange: setSearch, placeholder: 'Search description or entity' }}
        filters={[
          <FilterSelect
            key="type"
            value={typeFilter}
            onChange={(v) => setTypeFilter(v as 'all' | LogType)}
            options={[
              { value: 'all', label: 'All types' },
              ...TYPE_ORDER.map((t) => ({ value: t, label: TYPE_LABEL[t] })),
            ]}
          />,
        ]}
        actions={<ViewToggle value={view} onChange={setView} />}
        onClear={clear}
        hasActiveFilters={hasActiveFilters}
      />

      {error ? <div className="rounded-lg bg-clay/10 p-4 text-sm text-clay dark:bg-clay-dark/20 dark:text-clay-dark">{error}</div> : null}

      {loading ? (
        <Skeleton className="h-64 rounded-xl" />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={ScrollText}
          title={hasActiveFilters ? 'No matches' : 'No log entries yet'}
          description={hasActiveFilters ? 'Try clearing filters.' : 'Use Quick Log to capture work as it happens.'}
          action={
            hasActiveFilters ? <Button onClick={clear} variant="outline">Clear filters</Button> : <Button onClick={() => setQuickLogOpen(true)}><Plus className="h-4 w-4" />Log activity</Button>
          }
        />
      ) : (
        TYPE_ORDER.filter((t) => grouped[t].length > 0).map((type) => (
          <SectionHeader key={type} title={TYPE_LABEL[type]} count={grouped[type].length} collapsible>
            {view === 'grid' ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {grouped[type].map((l) => (
                  <Card
                    key={l.id}
                    title={l.entity_name || 'Untitled'}
                    subtitle={l.description}
                    badge={
                      <span className={`rounded-full px-2 py-0.5 font-mono text-2xs uppercase tracking-wider ${TYPE_TONE[l.log_type]}`}>
                        {TYPE_LABEL[l.log_type]}
                      </span>
                    }
                    footer={
                      <div className="flex items-center justify-between text-xs">
                        <span>{format(parseISO(l.date ?? l.created_at), 'MMM d, h:mm a')}</span>
                        <Trash2 className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                      </div>
                    }
                  />
                ))}
              </div>
            ) : (
              <div className="overflow-hidden rounded-xl border border-border bg-surface dark:bg-surface-dark dark:border-border">
                <table className="w-full text-sm">
                  <thead className="border-b border-border bg-surface-raised text-left dark:bg-surface-raised-dark">
                    <tr><Th>Date</Th><Th>Entity</Th><Th>Description</Th><Th>Type</Th></tr>
                  </thead>
                  <tbody>
                    {grouped[type].map((l) => (
                      <tr key={l.id} className="border-b border-border last:border-0">
                        <Td className="font-mono text-xs whitespace-nowrap">{format(parseISO(l.date ?? l.created_at), 'MMM d, h:mm a')}</Td>
                        <Td className="font-medium">{l.entity_name || '–'}</Td>
                        <Td className="text-muted-foreground">{l.description}</Td>
                        <Td>
                          <span className={`inline-flex rounded-full px-2 py-0.5 font-mono text-2xs uppercase tracking-wider ${TYPE_TONE[l.log_type]}`}>
                            {TYPE_LABEL[l.log_type]}
                          </span>
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionHeader>
        ))
      )}
    </div>
  );
}

function FilterSelect({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: { value: string; label: string }[] }): JSX.Element {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-9 rounded-md border border-border bg-surface px-2 text-sm shadow-sm dark:bg-surface-dark dark:border-border"
    >
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

function Th({ children }: { children: React.ReactNode }): JSX.Element {
  return <th className="px-4 py-2 font-mono text-2xs uppercase tracking-wider text-muted-foreground">{children}</th>;
}
function Td({ children, className }: { children: React.ReactNode; className?: string }): JSX.Element {
  return <td className={`px-4 py-3 ${className ?? ''}`}>{children}</td>;
}
